import { DataSource } from '@angular/cdk/collections';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { PaymentGroup } from '../../models/entities/payment-group';
import { PaymentGroupsService } from './payment-groups.service';
import { Moment } from 'moment-timezone';
import { PaymentGroups } from 'app/components/shared/http/request-interfaces';

export interface AdminPaymentsDatasourceOptions
{
    organizationId?: number;
    paymentMethodType?: string[];
    year?: number;
    startDate?: Moment;
    endDate?: Moment;
    csv?: number;
}

export const PaymentsPageSizeOptions = [
    25,
    50,
    100,
];

export class PaymentsDatasource implements DataSource<PaymentGroup>
{

    public loading$: Observable<boolean>;
    public count$: Observable<number>;
    public meta$: Observable<PaymentsPaginationMeta>;

    public filteringByCategory = false;
    private transactionSubject = new BehaviorSubject<PaymentGroup[]>([]);

    private loadingSubject = new BehaviorSubject<boolean>(false);

    private countSubject = new BehaviorSubject<number>(0);

    private metaSubject = new Subject<PaymentsPaginationMeta>();

    private cancelCurrentRequest = new Subject();

    constructor(private paymentGroupsService: PaymentGroupsService) {
        this.loading$ = this.loadingSubject.asObservable();
        this.count$ = this.countSubject.asObservable();
        this.meta$ = this.metaSubject.asObservable();
    }

    loadPayments(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ) {
        request.filters.csv = false;

        this.loadingSubject.next(true);

        this.filteringByCategory = request.filters.category ? true : false;

        this.cancelCurrentRequest.next(true);

        this.paymentGroupsService.getIncomingPaymentGroups(orgId, request)
                .pipe(takeUntil(this.cancelCurrentRequest))
                .pipe(
                        catchError((err) => of({data: [], meta: {total: 0}}).pipe(tap(_ => console.error(err)))),
                )
                .subscribe((charges: PaymentsResponse) => {
                    this.transactionSubject.next(charges.data);
                    const total = (charges.meta.total) ? charges.meta.total : 0;
                    this.metaSubject.next(charges.meta);
                    this.countSubject.next(total);
                    this.loadingSubject.next(false);
                });
    }

    /**
     * Dashboard variant — calls the enhanced endpoint that returns
     * per-status totals, method/source breakdowns, and monthly chart data in meta.
     */
    loadDashboardPayments(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ) {
        request.filters.csv = false;

        this.loadingSubject.next(true);

        this.filteringByCategory = request.filters.category ? true : false;

        this.cancelCurrentRequest.next(true);

        this.paymentGroupsService.getIncomingPaymentGroupsDashboard(orgId, request)
                .pipe(takeUntil(this.cancelCurrentRequest))
                .pipe(
                        catchError((err) => of({data: [], meta: {total: 0}}).pipe(tap(_ => console.error(err)))),
                )
                .subscribe((charges: PaymentsResponse) => {
                    this.transactionSubject.next(charges.data);
                    const total = (charges.meta.total) ? charges.meta.total : 0;
                    this.metaSubject.next(charges.meta);
                    this.countSubject.next(total);
                    this.loadingSubject.next(false);
                });
    }

    getCsv(
            orgId: number,
            request: PaymentGroups.GetIncomingPaymentGroupsRequest,
    ) {
        request.page = 1;
        request.filters.csv = true;
        return this.paymentGroupsService.getIncomingPaymentGroupsCsv(orgId, request);
    }

    connect(): Observable<PaymentGroup[]> {
        return this.transactionSubject.asObservable();
    }

    disconnect(): void {
        this.transactionSubject.complete();
        this.loadingSubject.complete();
        this.countSubject.complete();
    }

}

export class AdminPaymentsDatasource implements DataSource<PaymentGroup>
{

    public loading$: Observable<boolean>;
    public count$: Observable<number>;
    public meta$: Observable<PaymentsPaginationMeta>;

    public filteringByCategory = false;
    private transactionSubject = new BehaviorSubject<PaymentGroup[]>([]);

    private loadingSubject = new BehaviorSubject<boolean>(false);

    private countSubject = new BehaviorSubject<number>(0);

    private metaSubject = new BehaviorSubject<PaymentsPaginationMeta>(null);

    private cancelCurrentRequest = new Subject();

    constructor(private paymentGroupsService: PaymentGroupsService) {
        this.loading$ = this.loadingSubject.asObservable();
        this.count$ = this.countSubject.asObservable();
        this.meta$ = this.metaSubject.asObservable();
    }

    loadPayments(
            search: string = '',
            sortColumn: string = 'activeAfter',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: AdminPaymentsDatasourceOptions = {},
    ) {

        this.loadingSubject.next(true);

        this.cancelCurrentRequest.next(true);

        this.paymentGroupsService.getAdminPaymentGroups(search, sortColumn, sortDirection, pageIndex, perPage, options)
                .pipe(takeUntil(this.cancelCurrentRequest))
                .pipe(
                        catchError((err) => of({data: [], meta: {total: 0}}).pipe(tap(_ => console.error(err)))),
                )
                .subscribe((charges: PaymentsResponse) => {
                    this.transactionSubject.next(charges.data);
                    const total = (charges.meta.total) ? charges.meta.total : 0;
                    this.metaSubject.next(charges.meta);
                    this.countSubject.next(total);
                    this.loadingSubject.next(false);
                });
    }

    getCsv(
            search: string = '',
            sortColumn: string = 'activeAfter',
            sortDirection: string = 'desc',
            options: AdminPaymentsDatasourceOptions = {},
    ) {
        options.csv = 1;
        return this.paymentGroupsService.getAdminPaymentGroups(search, sortColumn, sortDirection, 1, -1, options);
    }

    connect(): Observable<PaymentGroup[]> {
        return this.transactionSubject.asObservable();
    }

    disconnect(): void {
        this.transactionSubject.complete();
        this.loadingSubject.complete();
        this.countSubject.complete();
    }

}


export class PaymentsResponse
{
    data: PaymentGroup[];
    meta: PaymentsPaginationMeta;

    constructor(request: any) {
        this.data = (request.data && request.data.length) ? request.data.map(tx => {
            return new PaymentGroup(tx);
        }) : [];
        this.meta = new PaymentsPaginationMeta(request.meta);
    }
}


export class PaymentsPaginationMeta
{
    currentPage: number;
    from: number;
    lastPage: number;
    path: string;
    perPage: number;
    to: number;
    total: number;
    original: number;
    totalFees: number;
    totalNet: number;
    totalAmount: number;
    categoryTotal: number;
    // Per-status aggregations
    successAmount: number;
    successCount: number;
    pendingAmount: number;
    pendingCount: number;
    returnedAmount: number;
    returnedCount: number;
    disputedAmount: number;
    disputedCount: number;
    // Breakdown aggregations
    methodBreakdown: Record<string, number>;
    sourceBreakdown: Record<string, number>;
    categoryBreakdown: { name: string; count: number; amount: number }[];
    monthlyTotals: { year: number; month: number; collected: number; succeeded?: number; pending?: number; returned?: number; disputed?: number }[];

    constructor(meta: any) {
        this.currentPage = meta.current_page || 0;
        this.from = meta.from || 0;
        this.lastPage = meta.last_page || 0;
        this.path = meta.path || null;
        this.perPage = meta.per_page || 0;
        this.to = meta.to || 0;
        this.total = meta.total || 0;
        this.original = meta.original || 0;
        this.totalFees = meta.totalFees || 0;
        this.totalNet = meta.totalNet || 0;
        this.totalAmount = meta.totalAmount || 0;
        this.categoryTotal = meta.categoryTotal || 0;
        this.successAmount = meta.successAmount || 0;
        this.successCount = meta.successCount || 0;
        this.pendingAmount = meta.pendingAmount || 0;
        this.pendingCount = meta.pendingCount || 0;
        this.returnedAmount = meta.returnedAmount || 0;
        this.returnedCount = meta.returnedCount || 0;
        this.disputedAmount = meta.disputedAmount || 0;
        this.disputedCount = meta.disputedCount || 0;
        this.methodBreakdown = meta.methodBreakdown || {};
        this.sourceBreakdown = meta.sourceBreakdown || {};
        this.categoryBreakdown = meta.categoryBreakdown || [];
        this.monthlyTotals = meta.monthlyTotals || [];
    }
}
